import { useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { SectionTitle } from "../../../components/SectionTitle";
import { formatDate, formatPeriod } from "../../../utils/dateUtils";
import { kickbackSourcePeriod } from "@KassaroAB/shared/utils/kickbackPeriod";
import { formatMoney } from "../../../utils/formatMoney";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { sumMoney } from "./kickbackTotals";
import type { KickbackSource } from "@KassaroAB/shared/types/enums/KickbackSource";
import type { RestKickbackSourceReport } from "@KassaroAB/shared/types/invoices/kickback";

const SourceCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const Stat = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};

  span:last-child {
    color: ${colors.text};
    font-weight: 500;
    text-align: right;
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  margin-top: auto;
`;

type KickbackSourceCardProps = {
  source: KickbackSource;
  invoicePeriod: string;
  report: RestKickbackSourceReport | undefined;
  isPending: boolean;
} & (
  | { kind: "viva"; onCalculate: (refresh: boolean) => void }
  | { kind: "report"; onUpload: (file: File) => void }
);

export const KickbackSourceCard = (props: KickbackSourceCardProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { source, invoicePeriod, report, isPending } = props;
  const unmatchedCount = report?.rows.filter((row) => row.resellerId === null).length ?? 0;

  return (
    <SourceCard>
      <SectionTitle>{t(`kickback.source.${source}`)}</SectionTitle>
      <Hint>{t(`kickback.sourceHint.${source}`)}</Hint>
      <Stat>
        <span>{t("kickback.dataPeriod")}</span>
        <span>{formatPeriod(kickbackSourcePeriod(invoicePeriod, source))}</span>
      </Stat>
      {report ? (
        <>
          <Stat>
            <span>{t("kickback.updatedAt")}</span>
            <span>{formatDate(report.generatedAt)}</span>
          </Stat>
          {report.fileName && (
            <Stat>
              <span>{t("kickback.file")}</span>
              <span>{report.fileName}</span>
            </Stat>
          )}
          <Stat>
            <span>{t("kickback.grossAmount")}</span>
            <span>{formatMoney(sumMoney(report.rows.map((row) => row.grossAmount)))}</span>
          </Stat>
          <Stat>
            <span>{t("kickback.profitShare")}</span>
            <span>{formatMoney(sumMoney(report.rows.map((row) => row.profitShare)))}</span>
          </Stat>
          <Stat>
            <span>{t("kickback.unmatched")}</span>
            <span>{unmatchedCount}</span>
          </Stat>
          {report.skippedRows.length > 0 && (
            <Stat>
              <span>{t("kickback.skippedRows")}</span>
              <span>{report.skippedRows.map((row) => row.reference).join(", ")}</span>
            </Stat>
          )}
        </>
      ) : (
        <Hint>{t("kickback.notLoaded")}</Hint>
      )}
      <Actions>
        {props.kind === "viva" ? (
          <>
            <Button disabled={isPending} onClick={() => props.onCalculate(false)}>
              {isPending ? t("kickback.fetching") : t("kickback.fetchViva")}
            </Button>
            {report && (
              <Button
                $variant="outlined"
                disabled={isPending}
                onClick={() => props.onCalculate(true)}
              >
                {t("kickback.refetchViva")}
              </Button>
            )}
          </>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) {
                  props.onUpload(file);
                }
              }}
            />
            <Button disabled={isPending} onClick={() => fileInputRef.current?.click()}>
              {isPending ? t("kickback.uploading") : t("kickback.uploadReport")}
            </Button>
          </>
        )}
      </Actions>
    </SourceCard>
  );
};
